import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';

import faqData from './pwa-faq.json';

const PWAFAQ = () => {
  const categoriesList = Object.keys(faqData);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const filteredFaqs = useMemo(() => {
    let faqs = [];

    // If no category selected, show all FAQs from all categories
    if (selectedCategories.length === 0) {
      categoriesList.forEach((cat) => {
        faqs = faqs.concat(faqData[cat] || []);
      });
    } else {
      selectedCategories.forEach((cat) => {
        faqs = faqs.concat(faqData[cat] || []);
      });
    }

    // Apply search filter
    return faqs.filter((faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [selectedCategories, searchTerm, categoriesList]);

  return (
    <Box sx={{ p: 2, mx: 'auto' }}>
      <TextField
        fullWidth
        placeholder='Search here...'
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        size='small'
        InputProps={{
          startAdornment: <Search sx={{ mr: 1 }} />,
          sx: { borderRadius: '20px' },
        }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {categoriesList.map((categoryItem) => (
          <Button
            key={categoryItem}
            variant={
              selectedCategories.includes(categoryItem)
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleCategoryToggle(categoryItem)}
            size='small'
            sx={{
              borderRadius: '15px',
              textTransform: 'capitalize',
              '&:hover': { boxShadow: 'none' },
              px: 3,
            }}
          >
            {categoryItem}
          </Button>
        ))}
      </Box>
      {filteredFaqs.length > 0 ? (
        filteredFaqs.map((faq) => (
          <Accordion
            key={faq.id}
            sx={{
              boxShadow: 'none',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: 0,
              py: 1,
              '&:last-child': { borderBottom: 'none' },
              '&.Mui-expanded': { margin: 0 },
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 },
              }}
            >
              <Typography
                fontWeight='bold'
                variant='body1'
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant='body1'
                dangerouslySetInnerHTML={{ __html: faq.answer }}
                sx={{ whiteSpace: 'pre-line' }}
              />
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Typography
          align='center'
          color='text.secondary'
          mt={2}
        >
          No FAQs found.
        </Typography>
      )}
    </Box>
  );
};

export default PWAFAQ;
